import React, { type ComponentType } from 'react';
import '@atlaskit/css-reset';
import { type EntryPointProps, usePreloadedQuery, graphql } from 'react-relay';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import { ProjectContextProvider } from '@atlassian/jira-business-entity-project-provider/src/ui/project-context/ProjectContextProvider.tsx';
import { ListFeaturesProvider } from '@atlassian/jira-business-list/src/controllers/features-context/index.tsx';
import ListView from '@atlassian/jira-business-list/src/index.tsx';
import { LIST_VIEW_PREFERENCES } from '@atlassian/jira-business-preferences/src/constants.tsx';
import { ViewPreferencesProvider } from '@atlassian/jira-business-preferences/src/controllers/view-preferences-context/index.tsx';
import type { formatRulesQuery as FormatRulesQuery } from '@atlassian/jira-relay/src/__generated__/formatRulesQuery.graphql';
import type { src_jiraBusinessListQuery } from '@atlassian/jira-relay/src/__generated__/src_jiraBusinessListQuery.graphql';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';

type JiraBusinessListAppType = EntryPointProps<
	{ listQuery: src_jiraBusinessListQuery; formattingRulesQuery: FormatRulesQuery },
	{},
	{},
	{}
>;

const JiraBusinessListApp: ComponentType<JiraBusinessListAppType> = ({
	queries: { listQuery, formattingRulesQuery },
}) => {
	const data = usePreloadedQuery<src_jiraBusinessListQuery>(
		graphql`
			query src_jiraBusinessListQuery($cloudId: ID!, $projectKey: String!) @preloadable {
				jira {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) {
						projectStyle @required(action: THROW)
						createIssues: action(type: CREATE_ISSUES) @required(action: THROW) {
							canPerform @required(action: THROW)
						}
						...ProjectContextProvider
					}
					isSubtasksEnabled(cloudId: $cloudId) @optIn(to: "JiraIsSubtasksEnabled")
				}
				...src_businessList_List_queryFragment
			}
		`,
		listQuery,
	);

	const isSimplifiedProject = data.jira?.jiraProjectByKey?.projectStyle === 'TEAM_MANAGED_PROJECT';
	const isSubtasksEnabled = data.jira?.isSubtasksEnabled ?? false;
	const canCreateIssues = data.jira?.jiraProjectByKey?.createIssues?.canPerform ?? false;
	const canCreateSubtask = canCreateIssues && (isSimplifiedProject || isSubtasksEnabled);

	return (
		<UFOSegment name="business-list-app">
			<AuthenticationCheck>
				<AppBase id="business">
					<ProjectContextProvider projectFragment={data.jira?.jiraProjectByKey}>
						<ListFeaturesProvider
							hasCreateSubtask={canCreateSubtask}
							canCreateIssues={canCreateIssues}
						>
							<ViewPreferencesProvider view="list" preferences={LIST_VIEW_PREFERENCES}>
								<ListView queryFragment={data} formattingRulesQuery={formattingRulesQuery} />
							</ViewPreferencesProvider>
						</ListFeaturesProvider>
					</ProjectContextProvider>
					<SubProductUpdater subProduct="core" />
				</AppBase>
			</AuthenticationCheck>
		</UFOSegment>
	);
};

export default JiraBusinessListApp;
